import API_BASE_URL, { API_TIMEOUT } from '@/config/apiConfig';
import { attachApiErrorMetadataFromResponseBody, getApiErrorMessage } from '@/utils/apiErrorMessage';
import axios, { AxiosInstance } from 'axios';
import {
    AuthResponse,
    Bundle,
    Customer,
    Employee,
    Item,
    PaginatedResponse,
    RecentActivity,
    ZeettuSummary
} from '../types/models';

// Enhanced API service with comprehensive endpoints
class ApiService {
  private readonly api: AxiosInstance;
  private refreshTokenPromise: Promise<string> | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
    });

    this.setupInterceptors();
  }
//
  private setupInterceptors() {
    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          // Backend expects x-auth-token header, not Authorization Bearer
          config.headers['x-auth-token'] = token;
        }
        return config;
      },
      (error) => Promise.reject(new Error(error.message || 'Request failed'))
    );

    // Response interceptor for token refresh with safe queuing
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            // Safe queuing: prevent multiple refresh attempts
            this.refreshTokenPromise ??= this.refreshAccessToken();
            
            const newToken = await this.refreshTokenPromise;
            this.refreshTokenPromise = null;
            
            if (newToken) {
              originalRequest.headers['x-auth-token'] = newToken;
              return this.api(originalRequest);
            }
          } catch (refreshError) {
            this.refreshTokenPromise = null;
            this.logout();
            window.location.href = '/login';
            throw refreshError;
          }
        }
        
        // Handle network errors (connection refused, timeout, etc.)
        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          // const baseUrl = this.api.defaults.baseURL;
          return Promise.reject(new Error(`Cannot connect to backend server . Please ensure the backend server is running.`));
        }
        
        // Handle timeout errors
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          return Promise.reject(new Error('Request timeout. The server is taking too long to respond.'));
        }
        
        if (error.response) {
          const msg = getApiErrorMessage(error, 'Request failed');
          const err = attachApiErrorMetadataFromResponseBody(new Error(msg), error.response.data);
          return Promise.reject(err);
        }
        return Promise.reject(new Error(error.message || 'Request failed'));
      }
    );
  }

  private getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }

  private getRefreshToken(): string | null {
    // In production, this would be an httpOnly cookie
    return localStorage.getItem('refreshToken');
  }

  private async refreshAccessToken(): Promise<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post(`${this.api.defaults.baseURL}/auth/refresh`, {
        refreshToken
      });
      
      const { accessToken } = response.data;
      localStorage.setItem('accessToken', accessToken);
      return accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      throw new Error('Token refresh failed');
    }
  }

  private logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
  }

  // Typed HTTP methods for convenience
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    console.log(`[API-SERVICE] Making GET request to: ${url}`);
    console.log(`[API-SERVICE] Request params:`, params);
    console.log(`[API-SERVICE] Request headers:`, {
      'company-id': 'test',
      'x-auth-token': this.getAccessToken() ? '***' : 'none'
    });
    
    const response = await this.api.get<T>(url, { 
      params,
      headers: {
        'company-id': 'test' // Add company-id header for all requests
      }
    });
    
    console.log(`[API-SERVICE] Response status:`, response.status);
    console.log(`[API-SERVICE] Response headers:`, response.headers);
    console.log(`[API-SERVICE] Raw response data:`, response.data);
    console.log(`[API-SERVICE] Response data type:`, typeof response.data);
    console.log(`[API-SERVICE] Response data keys:`, Object.keys(response.data || {}));
    
    return response.data;
  }

  // Fetch binary/blob resource and return an object URL for easy <img src>
  async getBlobUrl(url: string): Promise<string> {
    console.log(`[API-SERVICE] Making GET (blob) request to: ${url}`);
    const token = this.getAccessToken();
    const response = await this.api.get<Blob>(url, {
      responseType: 'blob',
      headers: {
        'company-id': 'test',
        ...(token ? { 'x-auth-token': token } : {})
      }
    });

    const blob = response.data;
    const objectUrl = URL.createObjectURL(blob);
    console.log('[API-SERVICE] Created object URL:', objectUrl);
    return objectUrl;
  }

  // async post<T>(url: string, data?: unknown): Promise<T> {
  //   console.log(`[API-SERVICE] Making POST request to: ${url}`);
  //   console.log(`[API-SERVICE] Request data type:`, typeof data);
  //   console.log(`[API-SERVICE] Request data:`, data);
    
  //   if (data instanceof FormData) {
  //     console.log(`[API-SERVICE] FormData contents:`);
  //     for (let [key, value] of data.entries()) {
  //       if (value instanceof File) {
  //         console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
  //       } else {
  //         console.log(`  ${key}: ${value}`);
  //       }
  //     }
  //   }
    
  //   console.log(`[API-SERVICE] Request headers:`, {
  //     'company-id': 'test',
  //     'x-auth-token': this.getAccessToken() ? '***' : 'none',
  //     'Content-Type': data instanceof FormData ? 'multipart/form-data' : 'application/json'
  //   });
    
  //   try {
  //     const response = await this.api.post<T>(url, data, {
  //       headers: {
  //         'company-id': 'test'
  //       }
  //     });
      
  //     console.log(`[API-SERVICE] Response status:`, response.status);
  //     console.log(`[API-SERVICE] Response headers:`, response.headers);
  //     console.log(`[API-SERVICE] Raw response data:`, response.data);
  //     console.log(`[API-SERVICE] Response data type:`, typeof response.data);
      
  //     return response.data;
  //   } catch (error: any) {
  //     console.error(`[API-SERVICE] POST request failed for ${url}:`, error);
  //     if (error.response) {
  //       console.error(`[API-SERVICE] Error response status:`, error.response.status);
  //       console.error(`[API-SERVICE] Error response data:`, error.response.data);
  //       console.error(`[API-SERVICE] Error response headers:`, error.response.headers);
  //     }
  //     throw error;
  //   }
  // }
  async post<T>(url: string, data?: unknown, config?: any): Promise<T> {
    console.log(`[API-SERVICE] Making POST request to: ${url}`);
    console.log(`[API-SERVICE] Request data type:`, typeof data);
    console.log(`[API-SERVICE] Request data:`, data);
  
    if (data instanceof FormData) {
      console.log(`[API-SERVICE] FormData contents:`);
      for (let [key, value] of data.entries()) {
        if (value instanceof File) {
          console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
        } else {
          console.log(`  ${key}: ${value}`);
        }
      }
    }
  
    // Build headers: always include company-id; include auth if available.
    const headers: Record<string, string> = { 'company-id': 'test' };
    const token = this.getAccessToken();
    if (token) headers['x-auth-token'] = token;
  
    // For FormData, omit Content-Type so the browser/axios sets multipart boundary.
    // For JSON, set application/json.
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // #region agent log
    if (data instanceof FormData) {
      fetch('http://127.0.0.1:7719/ingest/8c229f71-31d7-4bba-ab2a-177f9b57c95a', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'dcea36' },
        body: JSON.stringify({
          sessionId: 'dcea36',
          location: 'api.ts:post',
          message: 'POST FormData headers',
          data: {
            url,
            contentType: headers['Content-Type'] ?? '',
            hasBoundary: (headers['Content-Type'] ?? '').includes('boundary'),
          },
          timestamp: Date.now(),
          hypothesisId: 'F',
          runId: 'pre-fix',
        }),
      }).catch(() => {});
    }
    // #endregion
  
    console.log(`[API-SERVICE] Request headers:`, headers);
  
    try {
      const axiosConfig = { 
        headers,
        ...config // Merge any additional config (like timeout)
      };
      const response = await this.api.post<T>(url, data, axiosConfig);
      console.log(`[API-SERVICE] Response status:`, response.status);
      console.log(`[API-SERVICE] Response headers:`, response.headers);
      console.log(`[API-SERVICE] Raw response data:`, response.data);
      console.log(`[API-SERVICE] Response data type:`, typeof response.data);
  
      return response.data;
    } catch (error: any) {
      console.error(`[API-SERVICE] POST request failed for ${url}:`, error);
      // #region agent log
      fetch('http://127.0.0.1:7719/ingest/8c229f71-31d7-4bba-ab2a-177f9b57c95a', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'dcea36' },
        body: JSON.stringify({
          sessionId: 'dcea36',
          location: 'api.ts:post:catch',
          message: 'POST failed',
          data: {
            url,
            status: error.response?.status,
            dataType: typeof error.response?.data,
          },
          timestamp: Date.now(),
          hypothesisId: 'B',
          runId: 'pre-fix',
        }),
      }).catch(() => {});
      // #endregion
      if (error.response) {
        console.error(`[API-SERVICE] Error response status:`, error.response.status);
        console.error(`[API-SERVICE] Error response data:`, error.response.data);
        console.error(`[API-SERVICE] Error response headers:`, error.response.headers);

        const msg = getApiErrorMessage(error, 'Request failed');
        throw attachApiErrorMetadataFromResponseBody(new Error(msg), error.response.data);
      }
      throw error;
    }
  }
  

  async patch<T>(url: string, data?: unknown): Promise<T> {
    console.log(`[API-SERVICE] Making PATCH request to: ${url}`);
    // For FormData, omit Content-Type so the browser/axios sets the multipart boundary.
    const headers: Record<string, string> = { 'company-id': 'test' };
    if (!(data instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const response = await this.api.patch<T>(url, data, { headers });
    console.log(`[API-SERVICE] PATCH response:`, JSON.stringify(response.data, null, 2));
    return response.data;
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.api.put<T>(url, data, {
      headers: {
        'company-id': 'test'
      }
    });
    return response.data;
  }

  async delete<T>(url: string, data?: unknown): Promise<T> {
    const response = await this.api.delete<T>(url, {
      data,
      headers: {
        'company-id': 'test'
      }
    });
    return response.data;
  }

  async del<T>(url: string): Promise<T> {
    const response = await this.api.delete<T>(url, {
      headers: {
        'company-id': 'test'
      }
    });
    return response.data;
  }

  // Auth endpoints
  async login(credentials: { mobileNumber: string; password: string }): Promise<AuthResponse> {
    const response = await this.post<AuthResponse>('/admin/admin-management/login', credentials);
    
    // Store the idToken as accessToken for authentication
    if (response.data?.idToken) {
      localStorage.setItem('accessToken', response.data.idToken);
    }
    
    return response;
  }

  async register(employeeData: {
    isActive: boolean;
    firstName: string;
    lastName: string;
    mobileNumber: string;
    password: string;
    landNumber?: string;
    dob?: string;
    idNumber?: string;
    addressLine1?: string;
    addressLine2?: string;
    isMarried?: boolean;
    isMale?: boolean;
    roles: string[];
    profilePicture?: string;
  }): Promise<{ status: string; message: string | null; data: string }> {
    // Add company-id header as required by the backend API
    const response = await this.api.post<{ status: string; message: string | null; data: string }>('/admin/admin-management/register', employeeData, {
      headers: {
        'company-id': 'test' // This should be configurable in production
      }
    });
    return response.data;
  }

  async getCurrentUser(): Promise<Employee> {
    // The backend doesn't have a /auth/me endpoint
    // We'll return a mock user based on stored token data
    console.log('[API-SERVICE] getCurrentUser called - backend has no /auth/me endpoint');
    throw new Error('No current user endpoint available');
  }

  /** Verify current user password (e.g. before downloading sensitive reports). Throws on 401. */
  async verifyPassword(password: string): Promise<{ success: boolean }> {
    return this.post<{ success: boolean }>('/admin/admin-management/verify-password', { password });
  }

  // Customer endpoints
  async getCustomers(page = 1, limit = 20): Promise<PaginatedResponse<Customer>> {
    return this.get<PaginatedResponse<Customer>>('/customers', { page, limit });
  }

  async getCustomer(id: string): Promise<Customer> {
    return this.get<Customer>(`/customers/${id}`);
  }

  // Item endpoints
  async getItems(page = 1, limit = 20): Promise<PaginatedResponse<Item>> {
    return this.get<PaginatedResponse<Item>>('/items', { page, limit });
  }

  async getItem(id: string): Promise<Item> {
    return this.get<Item>(`/items/${id}`);
  }

  // Bundle endpoints
  async getBundles(page = 1, limit = 20): Promise<PaginatedResponse<Bundle>> {
    return this.get<PaginatedResponse<Bundle>>('/bundles', { page, limit });
  }

  async getBundle(id: string): Promise<Bundle> {
    return this.get<Bundle>(`/bundles/${id}`);
  }

  // Dashboard endpoints
  /** @deprecated Use dashboardService.getSummary */
  async getZeettuSummary(): Promise<ZeettuSummary> {
    return this.get<ZeettuSummary>('/dashboard/summary');
  }

  /** @deprecated Use dashboardService.getRecentActivity */
  async getRecentActivity(limit = 10): Promise<RecentActivity[]> {
    return this.get<RecentActivity[]>('/dashboard/recent-activity', { limit });
  }

  // Mock data for development (when VITE_USE_MOCKS=true)
  getMockCustomers(): Customer[] {
    return [
      
    ];
  }

  getMockItems(): Item[] {
    return [
      // {
      //   id: '1',
      //   name: 'Rice 5kg',
      //   description: 'Premium Basmati Rice',
      //   category: 'Food',
      //   unitPrice: 2500,
      //   stockQuantity: 100,
      //   minStockLevel: 20,
      //   isActive: true,
      //   sku: 'RICE-5KG',
      //   createdAt: '2024-01-01T00:00:00Z',
      //   updatedAt: '2024-01-01T00:00:00Z',
      //   whoAdded: 'admin'
      // },
      // {
      //   id: '2',
      //   name: 'Cooking Oil 1L',
      //   description: 'Sunflower Cooking Oil',
      //   category: 'Food',
      //   unitPrice: 450,
      //   stockQuantity: 50,
      //   minStockLevel: 10,
      //   isActive: true,
      //   sku: 'OIL-1L',
      //   createdAt: '2024-01-01T00:00:00Z',
      //   updatedAt: '2024-01-01T00:00:00Z',
      //   whoAdded: 'admin'
      // }
    ];
  }

  getMockBundles(): Bundle[] {
    return [
      {
        id: '1',
        name: 'Essential Food Pack',
        description: 'Basic food items for daily needs',
        items: [
          { itemId: '1', quantity: 1, unitPrice: 2500 },
          { itemId: '2', quantity: 2, unitPrice: 450 }
        ],
        totalPrice: 3400,
        isActive: true,
        createdAt: '2024-01-01T00:00:00Z',
        updatedAt: '2024-01-01T00:00:00Z',
        whoAdded: 'admin'
      }
    ];
  }

  getMockZeettuSummary(): ZeettuSummary {
    return {
      totalCustomers: 150,
      pendingDeliveries: 12,
      monthlyCollections: 250000,
      totalSamplesOut: 8,
      stockValue: 500000,
      activeZeettuCards: 120,
      activeInstallmentCards: 85,
      totalRevenue: 750000,
      averageOrderValue: 3500
    };
  }

  getMockRecentActivity(): RecentActivity[] {
    return [
      {
        id: '1',
        type: 'delivery',
        title: 'Delivery Completed',
        description: 'Package delivered to John Doe',
        timestamp: '2024-01-15T10:30:00Z',
        user: 'Delivery Staff',
        status: 'completed'
      },
      {
        id: '2',
        type: 'sample',
        title: 'Sample Issued',
        description: 'Rice sample issued to Jane Smith',
        timestamp: '2024-01-15T09:15:00Z',
        user: 'Sales Staff',
        status: 'out'
      }
    ];
  }
}

export const apiService = new ApiService();
